import { baseUrl } from "../constant/constant";
import { getCookie } from "./utils";

/* -------------------------------------------------------------------------
 * Ticket PDF download — one implementation for every surface that offers it.
 *
 * There are TWO endpoints, and picking between them by "am I signed in?" is
 * the bug this exists to prevent:
 *
 *   POST /download-tickets              auth:api, filters on ticketorders.id_user
 *   GET  /ticket-download/{confirmation_id}   public, trusts possession of the id
 *
 * A ticket bought WITHOUT signing in is stored with `id_user = 0` and belongs
 * to no account — 48 of the order anchors on this table look like that. The
 * authenticated endpoint can never match such a row, whoever is logged in, so
 * a viewer holding a token would get a 404 on a ticket they legitimately own
 * and are looking straight at.
 *
 * So: try the authenticated route when there's a token (it works for account
 * orders and returns the whole multi-attendee order), and fall back to the
 * confirmation-id route on ANY failure. The fallback is not a workaround for
 * the 404 — it is the correct route for a guest order.
 * ------------------------------------------------------------------------- */

/**
 * Fetch the PDF for an order and hand it to the browser as a save.
 *
 * @param {{id?: number|string, confirmation_id?: string}} ticket
 * @returns {Promise<void>} resolves once the download has been triggered
 * @throws {Error} when neither route can produce a file
 */
export async function downloadTicketPdf(ticket) {
    const orderId = ticket?.id;
    const confirmationId = ticket?.confirmation_id;

    if (!orderId && !confirmationId) {
        throw new Error("Download failed");
    }

    // Raw fetch() bypasses the axios ApiInterceptor, so the bearer token has
    // to be attached by hand.
    const token = getCookie("_aps_t") || localStorage.getItem("token");

    let res = null;

    if (token && orderId) {
        try {
            res = await fetch(`${baseUrl}/download-tickets`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ order_ids: [orderId] }),
            });
        } catch (err) {
            // Network-level failure — fall through to the public route rather
            // than surfacing an error we can still recover from.
            res = null;
        }
    }

    // 404 here means "not an order on this account", which a guest order always
    // is. 401 means the token expired mid-session. Both are recoverable when we
    // hold the confirmation id.
    if ((!res || !res.ok) && confirmationId) {
        res = await fetch(
            `${baseUrl}/ticket-download/${encodeURIComponent(confirmationId)}`
        );
    }

    if (!res || !res.ok) {
        throw new Error("Download failed");
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${confirmationId || "ticket"}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}
